import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/Button';
import type { ForceUpdateReason } from '../types';

type ForceUpdateScreenProps = {
  reason: ForceUpdateReason;
  onUpdate: () => void;
  updating?: boolean;
};

const REASON_KEY: Record<ForceUpdateReason, string> = {
  app_outdated: 'appOutdated',
  sync_schema_outdated: 'syncSchemaOutdated'
};

/**
 * Full-screen, non-dismissible block shown when the middleware reports the client
 * is unsupported (C.5 sets `reason`). Copy is localized per reason; the raw
 * backend `message` is never displayed. `onUpdate` is wired to the actual
 * service-worker update in C.6.
 */
export const ForceUpdateScreen = ({
  reason,
  onUpdate,
  updating = false
}: ForceUpdateScreenProps) => {
  const { t } = useTranslation();
  const key = REASON_KEY[reason];

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] grid place-items-center bg-shell/95 px-6 text-white"
    >
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-signal-warn/20">
          <AlertTriangle className="h-6 w-6 text-signal-warn" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-semibold">{t(`appVersion.force.${key}.title`)}</h1>
        <p className="mt-2 text-sm text-slate-300">{t(`appVersion.force.${key}.body`)}</p>
        <Button className="mt-6 w-full" onClick={onUpdate} disabled={updating}>
          {updating ? t('appVersion.force.updating') : t('appVersion.force.action')}
        </Button>
      </div>
    </div>
  );
};
